/* eslint-disable functional/immutable-data */
import { Container, Divider, IconButton } from "@fluidattacks/design";
import {
  type FC,
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTheme } from "styled-components";

import { useChatInputStore } from "../use-chat-input-store";
import { useChatStore } from "../use-chat-store";
import { translate } from "utils/translations/translate";

const defaultPrompts = Array.from({ length: 6 }, (_, index): string =>
  translate.t(`chat.suggestedPrompts.${index}`),
);

const InputMessage: FC<{ isExpanded: boolean }> = ({
  isExpanded,
}): JSX.Element => {
  const theme = useTheme();
  const stateBorder = {
    focus: `2px solid ${theme.palette.black}`,
    hover: `1px solid ${theme.palette.gray[600]}`,
    normal: `1px solid ${theme.palette.gray[100]}`,
  };
  const [state, setState] = useState<keyof typeof stateBorder>("normal");
  const { isLoading } = useChatStore();
  const { handlePrompt, handleSubmit, input, handleInputKeyDown } =
    useChatInputStore();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const placeholderRef = useRef<HTMLInputElement>(null);

  const onSubmit = useCallback((): void => {
    if (!defaultPrompts.includes(input)) {
      handleSubmit();
    }
  }, [handleSubmit, input]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
      if (!defaultPrompts.includes(input)) {
        handleInputKeyDown(event);
      }
    },
    [handleInputKeyDown, input],
  );

  const handleFocus = useCallback((): void => {
    setState("focus");
  }, []);

  const handleMouseEnter = useCallback((): void => {
    if (state !== "focus") {
      setState("hover");
    }
  }, [state]);

  const handleMouseLeave = useCallback((): void => {
    setState("normal");
  }, []);

  useEffect((): void => {
    const textarea = textareaRef.current;
    if (textarea) {
      if (input.trim().length > 0) {
        textarea.style.height = `${textarea.scrollHeight}px`;
      } else {
        textarea.style.height = "auto";
      }
    }
  }, [input]);

  useEffect((): void => {
    if (defaultPrompts.includes(input)) {
      placeholderRef.current?.focus();
    }
  }, [input]);

  return (
    <Fragment>
      <Divider mb={1.25} mt={0} />
      <Container
        alignItems={"center"}
        bgColor={theme.palette.gray[50]}
        borderColorHover={theme.palette.gray[800]}
        borderRadius={"16px"}
        bottom={"10px"}
        display={"flex"}
        gap={0.75}
        height={"fit-content"}
        justify={"space-between"}
        maxWidth={isExpanded ? "90%" : undefined}
        mx={1}
        onClick={handleFocus}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        px={1}
        py={1}
        style={{
          border: stateBorder[state],
          margin: isExpanded ? "0px auto" : undefined,
          maxWidth: isExpanded ? "800px" : undefined,
        }}
        width={"auto"}
      >
        <IconButton
          icon={"wand-magic-sparkles"}
          iconColor={theme.palette.gray[500]}
          iconSize={"lg"}
          iconType={"fa-light"}
          style={{
            background: "transparent",
            backgroundColor: "inherit",
            borderRadius: "50%",
            cursor: "unset",
            height: "20px",
            width: "20px",
          }}
          variant={"primary"}
        />
        <textarea
          disabled={isLoading}
          onChange={handlePrompt}
          onFocus={handleFocus}
          onKeyDown={onKeyDown}
          placeholder={"Ask anything..."}
          ref={textareaRef}
          rows={1}
          style={{
            background: "transparent",
            backgroundColor: "inherit",
            border: "none",
            color: theme.palette.gray[800],
            flex: 1,
            fontSize: "16px",
            height: "fit-content",
            outline: "none",
            overflowY: "hidden",
            padding: "8px 0",
            resize: "none",
            wordWrap: "break-word",
          }}
          value={input}
        />
        <IconButton
          aria-label={"Send"}
          disabled={[...defaultPrompts, ""].includes(input)}
          icon={"arrow-right"}
          iconColor={theme.palette.white}
          iconSize={"lg"}
          onClick={onSubmit}
          style={{
            borderRadius: "50%",
            height: "36px",
            width: "36px",
          }}
          variant={"primary"}
        />
      </Container>
    </Fragment>
  );
};

export { InputMessage };
